import React, { useState } from "react";
import {
  resolveLocalizedEntry,
  langAttribute,
} from "../../../utils/questionUtils";
import { strings } from "../../../resources/strings";
import {
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
  QuestionType,
  WorkflowName,
} from "../../../apis/API";
import { WorkflowSpec } from "./types";
import { serializeWorkflowAnswers } from "./workflowAnswers";
import { resolveSeedText } from "./historyPrefill";
import {
  resolveAddDetailsLabel,
  resolveFreeTextEntryTitle,
  withRequiredSuffix,
} from "./workflowLabels";
import { AnswerIcon, CHECK_PATH, saveButtonClass } from "./answerButtons";
import FloatingLabelField from "./FloatingLabelField";
import AnswerButtonRow from "./AnswerButtonRow";
import ChangeAnswerButton from "./ChangeAnswerButton";
import type { WorkflowAnswer } from "./WorkflowQuestion";

const wf = strings.reportForm.workflow;

export interface YesNoFreeTextFormProps {
  question: LocalizedParticipantQuestion;
  /** The resolved workflow name (non-null — the dispatcher resolved its spec). */
  workflowName: WorkflowName;
  /** The resolved YES_NO_FREE_TEXT spec (single `value` field). */
  spec: WorkflowSpec;
  /** Prior answer, for back-nav re-display (root choice + the entered detail). */
  defaultAnswer?: IdentifiableAnswerV3Input;
  /**
   * Raw `questionPrepopulation.values` for this workflow. Read only through the
   * `historyPrefill` helpers, which apply the history-eligibility list.
   */
  prepopulationValues?: string;
  onAnswer: (answer: WorkflowAnswer) => void;
}

/**
 * YES_NO_FREE_TEXT workflow UI (Android `FreeTextDetail`). A Yes/No root; "No"
 * records `answer:false` with an empty `workflowAnswers` and advances. "Yes"
 * opens a separate "Provide Details" step where the participant enters a
 * required, 1000-char-capped explanation; confirming records `answer:true` with
 * `workflowAnswers = {"value": "<text>"}`. The details step offers a "Change my
 * answer" control back to the Yes/No root (EHA-903).
 *
 * The explanation rides in `workflowAnswers.value` (the cross-client contract) —
 * NOT `textValue` (which is reserved for `questionType:text`) and NOT `notes`
 * (which is for `questionType:bool`).
 */
const YesNoFreeTextForm: React.FC<YesNoFreeTextFormProps> = ({
  question,
  workflowName,
  spec,
  defaultAnswer,
  prepopulationValues,
  onAnswer,
}) => {
  const questionId = question.id ?? "";
  // YES_NO_FREE_TEXT specs always carry exactly one field (`value`).
  const fieldKey = spec.fields[0].fieldKey;

  // The prior selection, reflected on the root Yes/No on re-entry. Derived (not
  // state): nothing toggles it in place — "No" advances away and "Yes" leaves
  // the root screen for the details step, so it only ever shows the seeded value.
  const rootAnswer = defaultAnswer?.answer ?? null;
  // Always open on the root Yes/No screen — per Figma, the detail screen is a
  // separate step and back/change is via the Question List menu (re-entering a
  // question remounts it here). Tapping Yes advances to the seeded details step.
  const [step, setStep] = useState<"root" | "details">("root");
  // Seeded once per mount from the in-session answer, else the participant's
  // last submitted answer (safe allowlist parse).
  const [detail, setDetail] = useState<string>(() =>
    resolveSeedText({ spec, workflowName, defaultAnswer, prepopulationValues }),
  );

  const headlineEntry = resolveLocalizedEntry(question.localization);
  const headline = headlineEntry?.text ?? "";
  // SC 3.1.2 — the resolver falls back across languages, so text can arrive in
  // a language the document does not declare. undefined when it matches.
  const headlineLang = langAttribute(headlineEntry?.locale);
  const entryTitle =
    resolveFreeTextEntryTitle(workflowName, question.workflowDefinition) ??
    headline;
  // Always resolves — `resolveAddDetailsLabel` falls back to the shared
  // `qf_common_add_details` catalog entry ("Add details below.").
  const detailLabel = resolveAddDetailsLabel(question.workflowDefinition);

  const handleNo = () => {
    // Clear any detail typed for a prior Yes so it can't resurface / re-emit.
    setDetail("");
    onAnswer({
      questionId,
      questionType: QuestionType.workflow,
      workflowName,
      answer: false,
      workflowAnswers: serializeWorkflowAnswers(workflowName, spec, []),
    });
  };

  const handleYes = () => {
    setStep("details");
  };

  const canSave = detail.trim().length > 0;

  // Save is gated by the button's `disabled` below (matches BoolQuestion /
  // TextQuestion), so no duplicate guard is needed here.
  const handleSave = () => {
    onAnswer({
      questionId,
      questionType: QuestionType.workflow,
      workflowName,
      answer: true,
      workflowAnswers: serializeWorkflowAnswers(workflowName, spec, [
        { [fieldKey]: detail.trim() },
      ]),
    });
  };

  if (step === "details") {
    return (
      <div
        data-testid="yes-no-free-text-form"
        className="flex flex-col gap-6 overflow-y-auto"
      >
        <h2 className="text-2xl text-black">{entryTitle}</h2>

        {/* The explanation is always required in the "Yes" branch (Save is
            gated on it), so mark it required (IHA-388). autoFocus moves focus to
            the field when the details step opens (the Yes button is unmounted). */}
        <FloatingLabelField
          multiline
          required
          autoFocus
          label={
            detailLabel
              ? withRequiredSuffix(detailLabel, true)
              : /* istanbul ignore next -- resolveAddDetailsLabel always resolves
                   via the qf_common_add_details catalog default; this branch is
                   only for the string|undefined return type. */ ""
          }
          value={detail}
          testId="free-text-detail"
          onChange={setDetail}
        />

        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className={saveButtonClass}
        >
          <AnswerIcon testId="save-continue-icon" path={CHECK_PATH} />
          {wf.saveContinue}
        </button>

        <ChangeAnswerButton onClick={() => setStep("root")} />
      </div>
    );
  }

  return (
    <div data-testid="yes-no-free-text-form" className="flex flex-col gap-6">
      <h2 lang={headlineLang} className="text-2xl text-black">
        {headline}
      </h2>

      <AnswerButtonRow
        onYes={handleYes}
        onNo={handleNo}
        yesSelected={rootAnswer === true}
        noSelected={rootAnswer === false}
      />
    </div>
  );
};

export default YesNoFreeTextForm;
